import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { MenuItem } from 'material-ui'
import moment from 'moment-timezone'
import AutoBookingPopoverContainer from './AutoBookingPopover'
import RegularBookingPopover from './RegularBookingPopover'
import { calculateAutoBookingTimes, formatTimeString } from './helpers'
import fetchApi from '../../../../services/fetchApi'
import { logException } from '../../../../services/sentry'

// Creating options for select fields (outside the component, because this needs to run only once)
// create chairs options for select fields
const chairSelectItems = []
for (let i = 1; i <= 10; i++) {
  chairSelectItems.push(<MenuItem value={i} primaryText={i} key={i} />)
}

class BookingPopover extends Component {
  constructor(props) {
    super(props)
    const {
      isNew,
      selectedBooking,
      selectedBooking: {
        automation,
        booking_patients: [bookingPatient] = [null],
        chairs,
        doctor_id: bookingDocId,
        end,
        location_id: bookingLocId,
        recurring,
        start,
        status,
      },
      doctors: [{ id: docId } = { id: null }],
      locations: [{ id: locId } = { id: null }],
    } = props
    const stateObj = {
      automation: false,
      appointmentTimes: [],
      chairs: 1,
      doctor: docId,
      end: start.clone().add(1, 'hour').format('H:mm'),
      location: locId,
      patient: bookingPatient,
      recurring,
      start: start.format('H:mm'),
      status,
    }
    if (!isNew) {
      if (bookingDocId) stateObj.doctor = bookingDocId
      stateObj.end = moment(end).format('H:mm')
      stateObj.chairs = chairs
      stateObj.automation = automation
      stateObj.location = bookingLocId
      if (automation) {
        stateObj.appointmentTimes = calculateAutoBookingTimes({
          ...selectedBooking,
        })
      }
    }
    this.state = {
      ...stateObj,
      autoAptTime: null,
      dialogOpen: false,
      edited: false,
      showBookingConfirm: false,
    }
  }
  onDeleteClick = () => {
    const {
      selectedBooking: { recurring },
    } = this.props
    if (recurring) {
      this.setState({ dialogOpen: true })
    } else {
      this.handleDeleteOne()
    }
  }

  onSaveClick = () =>
    this.props.selectedBooking.recurring
      ? this.setState({ recurring: false }, () => this.handleUpdateOne())
      : this.handleUpdateOne()

  closeDialog = () => this.setState({ dialogOpen: false })

  handleValueUpdate = (field, nextValue) =>
    this.setState({
      [field]: nextValue,
      edited: true,
    })

  deleteBooking = async (bookingId, route) => {
    const { fetchBookings, onClose } = this.props
    try {
      this.closeDialog()
      await fetchApi(`/${route}/${bookingId}`, 'DELETE')
      fetchBookings()
      onClose(true)
    } catch (error) {
      logException(error)
      fetchBookings()
      onClose()
    }
  }

  createBooking = (booking, route) =>
    fetchApi(`/${route}`, 'POST', { body: booking })
      .then(() => {
        this.props.fetchBookings()
        this.props.onClose()
      })
      .catch(err => {
        logException(err)
        this.props.fetchBookings()
        this.props.onClose()
      })

  updateBooking = (bookingId, route) => {
    const booking = this.formatBooking()
    return fetchApi(`/${route}/${bookingId}`, 'PUT', { body: booking })
      .then(() => {
        this.props.fetchBookings().then(() => {
          this.setState({ showBookingConfirm: true })
          this.props.onClose()
        })
      })
      .catch(err => {
        logException(err)
        this.props.fetchBookings()
        this.props.onClose()
      })
  }

  formatRegularBooking = () => {
    const { start, end, doctor, location, recurring, chairs } = this.state
    const { selectedBooking } = this.props
    const hhmmStart = start.split(':')
    const hhmmEnd = end.split(':')
    const length =
      +hhmmEnd[0] * 60 + +hhmmEnd[1] - (+hhmmStart[0] * 60 + +hhmmStart[1])
    const startDatetime = formatTimeString(start, selectedBooking.start)
    return JSON.stringify({
      location_id: location,
      doctor_id: doctor,
      start_datetime: startDatetime,
      length,
      recurring,
      chairs,
    })
  }

  formatAutoBooking = () => {
    const { selectedBooking } = this.props
    const { autoAptTime, doctor } = this.state
    if (selectedBooking.status === 'booked') return this.formatRegularBooking()
    const startDatetime = formatTimeString(autoAptTime, selectedBooking.start)
    return JSON.stringify({
      doctor_id: doctor || this.props.doctors[0].id,
      length: 60,
      start_datetime: startDatetime,
    })
  }

  formatBooking = () =>
    this.props.isNew ||
    !this.state.automation ||
    this.state.status !== 'reserved'
      ? this.formatRegularBooking()
      : this.formatAutoBooking()

  handleCreate = () => {
    const newBooking = this.formatBooking()
    const route = this.state.recurring ? 'recurring_bookings' : 'bookings'
    return this.createBooking(newBooking, route)
  }

  handleDeleteOne = () =>
    this.deleteBooking(this.props.selectedBooking.id, 'bookings')

  handleDeleteAll = () =>
    this.deleteBooking(
      this.props.selectedBooking.recurring_booking_id,
      'recurring_bookings',
    )

  handleUpdateOne = () => {
    this.updateBooking(this.props.selectedBooking.id, 'bookings')
  }

  truncateLocationText = name =>
    name.length > 19 ? `${name.slice(0, 16)}...` : name

  renderDoctorOptions = () =>
    this.props.doctors.map(doctor => (
      <MenuItem
        value={doctor.id}
        primaryText={doctor.preferred_name}
        key={doctor.id}
      />
    ))

  renderLocationOptions = () =>
    this.props.locations.map(location => (
      <MenuItem
        value={location.id}
        primaryText={this.truncateLocationText(location.name)}
        key={location.id}
      />
    ))

  render() {
    const { doctors, leaveModalOpen, onClose, selectedBooking, locations } =
      this.props
    const {
      autoAptTime,
      appointmentTimes,
      doctor,
      location,
      patient,
      showBookingConfirm,
    } = this.state
    return selectedBooking.automation &&
      selectedBooking.status === 'reserved' ? (
        <AutoBookingPopoverContainer
          autoAptTime={autoAptTime}
          appointmentTime={autoAptTime}
          appointmentTimes={appointmentTimes}
          doctors={doctors}
          doctor={doctor}
          handleValueUpdate={this.handleValueUpdate}
          selectedBooking={selectedBooking}
          leaveModalOpen={leaveModalOpen}
          locations={locations}
          location={location}
          onClose={onClose}
          onSaveClick={this.onSaveClick}
          onDeleteClick={this.onDeleteClick}
          patient={patient}
          renderDoctorOptions={this.renderDoctorOptions}
          showBookingConfirm={showBookingConfirm}
          startDateTime={selectedBooking.start_datetime}
        />
    ) : (
      <RegularBookingPopover
        {...this.state}
        {...this.props}
        chairSelectItems={chairSelectItems}
        closeDialog={this.closeDialog}
        dayOfWeek={selectedBooking.start.format('dddd')}
        handleCreate={this.handleCreate}
        handleDeleteOne={this.handleDeleteOne}
        handleDeleteAll={this.handleDeleteAll}
        handleValueUpdate={this.handleValueUpdate}
        onDeleteClick={this.onDeleteClick}
        onSaveClick={this.onSaveClick}
        renderDialog={this.renderDialog}
        renderDoctorOptions={this.renderDoctorOptions}
        renderLocationOptions={this.renderLocationOptions}
      />
    )
  }
}

BookingPopover.propTypes = {
  doctors: PropTypes.arrayOf(PropTypes.object),
  fetchBookings: PropTypes.func.isRequired,
  isNew: PropTypes.bool,
  leaveModalOpen: PropTypes.func.isRequired,
  locations: PropTypes.arrayOf(PropTypes.object),
  onClose: PropTypes.func.isRequired,
  selectedApptType: PropTypes.string.isRequired,
  selectedBooking: PropTypes.shape({
    appointments: PropTypes.array,
    automation: PropTypes.bool,
    booked_chairs: PropTypes.number,
    chairs: PropTypes.number,
    colIndex: PropTypes.number,
    created_at: PropTypes.string,
    end: PropTypes.object,
    from_pms: PropTypes.bool,
    id: PropTypes.number,
    length: PropTypes.number,
    location_id: PropTypes.string,
    numCols: PropTypes.number,
    patient: PropTypes.object,
    recurring: PropTypes.bool,
    recurring_booking_id: PropTypes.number,
    start: PropTypes.object,
    start_datetime: PropTypes.string,
    status: PropTypes.string,
    updated_at: PropTypes.string,
  }),
}

export default BookingPopover



// WEBPACK FOOTER //
// ./src/components/OnlineScheduling/BookingsCalendar/BookingPopover/index.js